import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
  Fade,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useSections } from "../../hooks/useSections/useGetSections";
import DocumentStylesEditor from "./DocumentStylesEditor";
import ConfigurationContent from "./configurationSyles/ConfigurationcontentEditor";

export const StylesPanel = ({ token, idDocument, nameDocument, date }) => {
  const theme = useTheme();
  const scrollableRef = useRef(null);

  const { loading } = useSections(token, idDocument);
  const [currentView, setCurrentView] = useState("list");
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo(0, 0);
    }
  }, [currentView]);

  const handleEditOption = (option) => {
    setSelectedOption(option);
    setCurrentView(option.type === "sections" ? "edit" : "full");
  };

  const handleBackToList = () => {
    setCurrentView("list");
    setSelectedOption(null);
  };

  const navigationOptions = [
    {
      id: "full-document",
      title: "Estilos - Documento Completo",
      description: "Edita los estilos generales del documento",
      icon: Edit,
      type: "full",
    },
    {
      id: "section-document",
      title: "Estilos - Por Secciones",
      description: "Crea y edita los estilos de cada sección del documento",
      icon: Edit,
      type: "sections",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.default,
      }}
    >
      <Box ref={scrollableRef} sx={scrollableBoxStyles(theme)}>
        {loading && currentView === "list" ? (
          <LoadingSkeleton />
        ) : currentView === "edit" ? (
          <Fade in timeout={300}>
            <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
              <DocumentStylesEditor
                section={selectedOption}
                onBack={handleBackToList}
                theme={theme}
                nameDocument={nameDocument}
                idDocument={idDocument}
                token={token}
              />
            </Box>
          </Fade>
        ) : currentView === "full" ? (
          <Fade in timeout={300}>
            <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
              <ConfigurationContent
                token={token}
                idDocument={idDocument}
                onBack={handleBackToList}
                onSwitchTab={() => handleEditOption({ type: "sections" })}
                theme={theme}
                nameDocument={nameDocument}
                date={date}
              />
            </Box>
          </Fade>
        ) : (
          <Fade in timeout={300}>
            <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: { xs: 1, sm: 2 } }}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {navigationOptions.map((option) => (
                  <SectionCard
                    key={option.id}
                    option={option}
                    onEdit={handleEditOption}
                    theme={theme}
                  />
                ))}
              </Grid>
            </Box>
          </Fade>
        )}
      </Box>
    </Paper>
  );
};

const SectionCard = ({ option, onEdit, theme }) => {
  const IconComponent = option.icon || Edit;

  return (
    <Grid item xs={12} sm={6}>
      <Card
        sx={{
          borderRadius: "12px",
          backgroundColor: theme.palette.background.paper,
          transition: "all 0.3s ease",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
          "&:hover": {
            transform: { sm: "translateY(-4px)" },
            boxShadow: { sm: theme.shadows[6] },
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Box
              sx={{
                width: { xs: 8, sm: 12 },
                height: { xs: 8, sm: 12 },
                borderRadius: "50%",
                backgroundColor: "success.main",
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  textTransform: "capitalize",
                  letterSpacing: "0.4px",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                {option.title}
              </Typography>
              {option.description && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>
          </Box>

          <Tooltip title="Editar" arrow>
            <IconButton
              onClick={() => onEdit(option)}
              sx={{
                backgroundColor: "transparent",
                color: theme.palette.primary.main,
                borderRadius: "8px",
                p: { xs: 0.7, sm: 1 },
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: { sm: theme.palette.primary.main },
                  color: { sm: theme.palette.primary.contrastText },
                  boxShadow: { sm: `0 0 0 4px ${theme.palette.primary.light}` },
                },
                transition: "all 0.2s ease-out",
              }}
            >
              <IconComponent fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>
    </Grid>
  );
};

const LoadingSkeleton = () => (
  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
    {Array.from({ length: 2 }).map((_, i) => (
      <Grid item xs={12} sm={6} key={i}>
        <Skeleton
          variant="rectangular"
          height={70}
          sx={{
            borderRadius: "12px",
            width: "100%",
          }}
        />
      </Grid>
    ))}
  </Grid>
);

const scrollableBoxStyles = (theme) => ({
  p: { xs: 1.5, sm: 3 },
  overflowY: "auto",
  flexGrow: 1,
  height: { xs: "calc(100vh - 160px)", sm: "calc(100vh - 200px)" },
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: 6,
    height: 6,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.1)"
        : "rgba(0,0,0,0.1)",
    borderRadius: 3,
  },
});
